import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { apiWorkerManager } from "../../../workerManagers/workerManager";

/**
 * Common fields shared by every final-documents BigQuery log helper.
 *
 * These are supplied as pre-extracted primitives so callers can source them
 * from any backend (Postgres accumulator, Firestore slice, etc.) without this
 * module depending on a specific order shape.
 *
 * @property patientId - Patient id for the event row
 * @property orderId - Order id for the event row
 * @property payerName - Primary coverage payer name
 * @property regimenName - Regimen name from the order payload
 * @property orgId - Organisation id
 * @property userId - Acting user id
 * @property userEmail - Acting user email
 * @property additionalData - Extra event-specific fields merged into `additional_data`
 */
interface BaseLogEventParams {
  patientId: string;
  orderId: string;
  payerName: string;
  regimenName: string;
  orgId: string;
  userId: string;
  userEmail: string;
  additionalData?: Record<string, any>;
}

interface LogEventParams extends BaseLogEventParams {
  eventName: EventsName;
}

/**
 * Logs events to BigQuery with common parameters for final documents screen
 * @param params - Event logging parameters
 */
export const logFinalDocumentEvent = async ({
  eventName,
  patientId,
  orderId,
  payerName,
  regimenName,
  orgId,
  userId,
  userEmail,
  additionalData = {},
}: LogEventParams) => {
  try {
    await apiWorkerManager.enqueueAndProcess({
      patient_id: patientId ?? "",
      order_id: orderId ?? "",
      org_id: orgId ?? "",
      event_name: eventName,
      user_id: userId ?? "",
      user_email: userEmail ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
        payer_name: payerName ?? "",
        regimen_name: regimenName ?? "",
        ...additionalData,
      },
    });
  } catch (error) {
    console.error("Failed to log event to BigQuery:", error);
  }
};

/**
 * Logs submit form click event
 */
export const logSubmitFormClick = async (params: BaseLogEventParams) => {
  await logFinalDocumentEvent({
    ...params,
    eventName: EventsName.SUBMIT_FORM_CLICKED,
    additionalData: {
      source: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
      ...params.additionalData,
    },
  });
};

/**
 * Logs uploading attachment completed event
 */
export const logUploadingAttachmentCompleted = async (
  params: BaseLogEventParams,
) => {
  await logFinalDocumentEvent({
    ...params,
    eventName: EventsName.UPLOADING_ATTACHMENT_COMPLETED,
  });
};

/**
 * Logs submission initiated event
 */
export const logSubmissionInitiated = async (params: BaseLogEventParams) => {
  await logFinalDocumentEvent({
    ...params,
    eventName: EventsName.SUBMISSION_INITIATED,
    additionalData: {
      source: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
      ...params.additionalData,
    },
  });
};

/**
 * Logs manage attachments clicked event
 */
export const logManageAttachmentsClick = async (params: BaseLogEventParams) => {
  await logFinalDocumentEvent({
    ...params,
    eventName: EventsName.MANAGE_ATTACHMENTS_CLICKED,
    additionalData: {
      source: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
      ...params.additionalData,
    },
  });
};
